import { yupResolver } from "@hookform/resolvers/yup";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { Input } from "@src/components/input/Input";
import { Select } from "@src/components/select/Select";
import { pricingActions } from "@src/store";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { SchedulePriceValidation, SchedulePriceValidationType } from "@src/validation/SchedulePriceValidation";
import React, { useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "sonner";
import { IAdvancedTab } from "../../AdvancedTab";
import { SubHeader } from "../SubHeader/SubHeader";
import { AmPm } from "./AmPm";
import styles from "./SchedulePrice.module.scss";
import { schedulePriceFormat } from "./schedulePriceFormat";
import { HighlightedSectionEnum } from "@src/store/pricing/selectionSlice";
import useHighlightedSection from "@src/hooks/useHighlightedSection";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";
import { schedulePriceformatTime, schedulePriceParseEventTime } from "@src/components/utils/dateUtils";
import DeleteSchedulePriceModal from "./DeleteSchedulePriceModal";
import { TListingPricingSchedule, useListingPricingSchedule } from "@src/hooks/useListingPricingSchedule";
import { Loader } from "@src/components/loader/Loader";

type TSchedulePrice = IAdvancedTab & {
	tableView?: boolean;
	defaultValues?: TListingPricingSchedule | null;
	onSuccess?: () => void;
};

const parseScheduleDateTime = (value?: string | null) => {
	if (!value) return null;

	const [datePart, timePart = "00:00:00"] = value.split("T");
	const [year, month, day] = datePart.split("-").map(Number);
	const [hour24, minute] = timePart.split(":").map(Number);

	const abbreviation = hour24 >= 12 ? "PM" : "AM";
	const hour12 = hour24 % 12 || 12;

	return {
		date: new Date(year, month - 1, day),
		hour: hour12,
		minute,
		abbreviation,
	};
};

const mapSchedulePricingToFormValues = (
	data: TListingPricingSchedule | null | undefined,
): Partial<SchedulePriceValidationType> => {
	if (!data) return {};

	const start = parseScheduleDateTime(data.Start_Date_Time_EST);
	const end = parseScheduleDateTime(data.End_Date_Time_EST);

	return {
		startPrice: data.Start_Price,
		endPrice: data.End_Price,
		alterPriceBy: data.Alter_Amount,
		cycle: String(data.Price_Cycle_Minutes),
		startDate: start?.date,
		endDate: end?.date,
		startTimeHour: start?.hour,
		startTimeMinute: start?.minute,
		startTimeAbbreviation: start?.abbreviation as "AM" | "PM" | undefined,
		endTimeHour: end?.hour,
		endTimeMinute: end?.minute,
		endTimeAbbreviation: end?.abbreviation as "AM" | "PM" | undefined,
	};
};

const SchedulePriceInner = ({ inventoryItem, tableView, onSuccess, defaultValues }: TSchedulePrice) => {
	const dispatch = useAppDispatch();

	const { Event_Time, Event_Date } = useAppSelector((state) => state.pricing.selection.event) ?? {};

	const schedulePriceLoading = useAppSelector((state) => state.pricing.schedulePrice.loading);

	const { loading: updateListingPriceLoading } = useAppSelector((state) => state.pricing.updateListingPrices);

	const currentDateRef = React.useRef(new Date());
	const currentDate = currentDateRef.current;

	const [numberOfPriceChanges, setNumberOfPriceChanges] = React.useState(0);

	const endDateInner = Event_Date ? new Date(Event_Date) : undefined;

	const endTimeParsed = schedulePriceParseEventTime(Event_Time ?? "");
	const startTimeParsed = schedulePriceformatTime(currentDate);

	const schedulePriceDefaultValues = useMemo(() => {
		const baseValues: SchedulePriceValidationType = {
			startPrice: inventoryItem?.Item_Price ?? 0,
			endPrice: (inventoryItem?.Item_Price ?? 0) * 0.7,
			alterPriceBy: 0,
			cycle: "30",
			startDate: currentDate,
			endDate: endDateInner,
			startTimeHour: startTimeParsed.hour,
			startTimeMinute: startTimeParsed.minute,
			startTimeAbbreviation: startTimeParsed.abbreviation,
			endTimeHour: endTimeParsed.hour12,
			endTimeMinute: endTimeParsed.minute,
			endTimeAbbreviation: endTimeParsed.abbreviation,
		};

		const mapped = mapSchedulePricingToFormValues(defaultValues);

		return {
			...baseValues,
			...mapped,
		};

		// eslint-disable-next-line
	}, [inventoryItem, Event_Time, Event_Date, defaultValues]);

	const {
		handleSubmit,
		control,
		watch,
		setValue,
		reset,
		formState: { isValid },
	} = useForm<SchedulePriceValidationType>({
		resolver: yupResolver(SchedulePriceValidation),
		mode: "onChange",
		defaultValues: schedulePriceDefaultValues,
	});

	useEffect(() => {
		reset(schedulePriceDefaultValues);
	}, [schedulePriceDefaultValues, reset]);

	const startDate = watch("startDate");
	const endDate = watch("endDate");
	const startTimeAbbreviation = watch("startTimeAbbreviation");
	const endTimeAbbreviation = watch("endTimeAbbreviation");

	const isSchedulePriceEnabled = inventoryItem?.Is_Scheduled_Pricing;

	const onChange = (dates: [Date | null, Date | null]) => {
		const [start, end] = dates;
		setValue("startDate", start ?? undefined, { shouldDirty: true, shouldValidate: true });
		setValue("endDate", end ?? undefined, { shouldDirty: true, shouldValidate: true });
	};

	const onSubmit: SubmitHandler<SchedulePriceValidationType> = async (data) => {
		const listingId = inventoryItem?.Listing_ID;
		if (!listingId) return;

		const dataToSend = schedulePriceFormat(data, listingId);
		if (!dataToSend) return;

		mixpanelTrack(MixpanelCategory.SCHEDULE_PRICING);

		try {
			await dispatch(pricingActions.updateSchedulePrice(dataToSend));
			await dispatch(
				pricingActions.updateListingPrices([
					{
						listing_id: dataToSend.listingIds[0],
						price: dataToSend.startPrice,
						face: null,
					},
				]),
			);
			toast.success("Schedule Price successful");
			onSuccess?.();
		} catch (error) {
			toast.warning("Error while processing your request");
			console.log(error);
		}
	};

	const handleAltrBuyCalculation = () => {
		const startPrice = Number(watch("startPrice"));
		const endPrice = Number(watch("endPrice"));
		const cycleMinutes = Number(watch("cycle"));

		const startDate = watch("startDate");
		const endDate = watch("endDate");

		const sh = Number(watch("startTimeHour"));
		const sm = Number(watch("startTimeMinute"));
		const sa = watch("startTimeAbbreviation");

		const eh = Number(watch("endTimeHour"));
		const em = Number(watch("endTimeMinute"));
		const ea = watch("endTimeAbbreviation");

		if (!startDate || !endDate || !cycleMinutes || !isFinite(startPrice) || !isFinite(endPrice)) return;

		const to24 = (h: number, abbr: string) => {
			if (abbr === "PM" && h < 12) return h + 12;
			if (abbr === "AM" && h === 12) return 0;
			return h;
		};

		const start = new Date(startDate);
		start.setHours(to24(sh, sa), sm, 0, 0);

		const end = new Date(endDate);
		end.setHours(to24(eh, ea), em, 0, 0);

		const diffMinutes = Math.floor((end.getTime() - start.getTime()) / 60000);

		if (diffMinutes <= 0) {
			setNumberOfPriceChanges(0);
			return;
		}

		const numberOfChanges = Math.floor(diffMinutes / cycleMinutes);

		if (numberOfChanges <= 0) {
			setNumberOfPriceChanges(0);
			return;
		}

		const maxDelta = endPrice - startPrice;
		const alterBy = maxDelta / numberOfChanges;

		if (!isFinite(alterBy)) {
			setNumberOfPriceChanges(0);
			return;
		}

		setNumberOfPriceChanges(numberOfChanges);
		setValue("alterPriceBy", Number(alterBy.toFixed(4)), { shouldDirty: true });
	};

	const canCalculate = numberOfPriceChanges >= 0 && !!startDate && !!endDate;

	const onReset = () => {
		setNumberOfPriceChanges(0);
		reset(
			{
				startPrice: undefined,
				endPrice: undefined,
				alterPriceBy: 0,
				cycle: "30",
				startDate: undefined,
				endDate: undefined,
				startTimeHour: 0,
				startTimeMinute: 0,
				startTimeAbbreviation: "AM",
				endTimeHour: 0,
				endTimeMinute: 0,
				endTimeAbbreviation: "PM",
			},
			{ keepDirty: false },
		);
	};

	const onStartAbbreviationClick = (x: string) => {
		setValue("startTimeAbbreviation", x, { shouldDirty: true });
	};

	const onEndAbbreviationClick = (x: string) => {
		setValue("endTimeAbbreviation", x, { shouldDirty: true });
	};

	const cycleOptions = [
		{ label: "30 Minutes", value: "30" },
		{ label: "1 Hour", value: "60" },
		{ label: "2 Hours", value: "120" },
		{ label: "6 Hours", value: "360" },
		{ label: "1 Day", value: "1440" },
		{ label: "2 Days", value: "2880" },
		{ label: "7 Days", value: "10080" },
	];

	const timeInputStyle = {
		width: "80px",
		fontSize: "2rem",
		padding: "3px 10px",
	};

	const isHighlighted = useHighlightedSection(HighlightedSectionEnum.SchedulePrice);

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			style={{ padding: tableView ? "20px 0px" : undefined }}
			className={styles.schedulePriceMain}>
			{tableView ? null : <SubHeader isHighlighted={isHighlighted} title="Schedule Price" tooltipText="Schedule Price" />}

			<div className={styles.inputs}>
				<div>
					<Input
						fixToDecimals
						inputPrefix="$"
						label="Start Price"
						type="number"
						control={control}
						name="startPrice"
						step={1}
					/>
				</div>
				<div>
					<Input fixToDecimals inputPrefix="$" label="End Price" type="number" control={control} name="endPrice" step={1} />
				</div>
				<div>
					<Input label="Alter Price by" type="number" control={control} name="alterPriceBy" step={1} />
				</div>
				<div>
					<Select label="Cycle" name="cycle" control={control} options={cycleOptions} />
				</div>
			</div>

			<div data-is-={tableView} className={styles.calendarContainer}>
				<div className={styles.calendar}>
					<DatePicker
						selectsRange
						startDate={startDate ?? undefined}
						endDate={endDate ?? undefined}
						onChange={onChange}
						minDate={new Date()}
						inline
						showDisabledMonthNavigation
					/>
				</div>

				<div className={styles.calendarRight}>
					<div className={styles.calendarInputs}>
						<div className={styles.inputHolder}>
							<Input
								disableXbutton
								style={timeInputStyle}
								label="Start Time (EST)"
								type="number"
								control={control}
								name="startTimeHour"
							/>
							<div className={styles.twoDots}>:</div>
							<Input disableXbutton style={timeInputStyle} type="number" control={control} name="startTimeMinute" />
							<AmPm onClick={onStartAbbreviationClick} selected={startTimeAbbreviation} value1="AM" value2="PM" />
						</div>

						<div className={styles.inputHolder}>
							<Input
								disableXbutton
								style={timeInputStyle}
								label="End Time (EST)"
								type="number"
								control={control}
								name="endTimeHour"
							/>
							<div className={styles.twoDots}>:</div>
							<Input disableXbutton style={timeInputStyle} type="number" control={control} name="endTimeMinute" />
							<AmPm onClick={onEndAbbreviationClick} selected={endTimeAbbreviation} value1="AM" value2="PM" />
						</div>
					</div>

					<div className={styles.buttonContainer}>
						<div className={styles.alterByButtonContainer}>
							<div className={styles.alterByText}>Number of price changes: {numberOfPriceChanges}</div>
							<Button
								type="button"
								onClick={handleAltrBuyCalculation}
								variant={ButtonVariant.Secondary}
								controlSize="sm"
								disabled={!canCalculate}>
								Calculate Alter Price By
							</Button>
						</div>

						<div className={styles.buttons}>
							<Button
								isLoading={schedulePriceLoading || updateListingPriceLoading}
								type="button"
								onClick={onReset}
								variant={ButtonVariant.Secondary}
								controlSize="sm">
								Reset
							</Button>

							{isSchedulePriceEnabled ? (
								<DeleteSchedulePriceModal ListingIds={[inventoryItem?.Listing_ID]} onSuccess={onSuccess} />
							) : null}

							<Button
								isLoading={schedulePriceLoading || updateListingPriceLoading}
								type="submit"
								variant={ButtonVariant.Primary}
								controlSize="sm"
								disabled={!isValid}>
								Schedule Pricing
							</Button>
						</div>
					</div>
				</div>
			</div>
		</form>
	);
};

export const SchedulePrice = ({ inventoryItem, tableView, onSuccess }: TSchedulePrice) => {
	const listingId = inventoryItem?.Listing_ID;
	const isSchedulePriceEnabled = inventoryItem?.Is_Scheduled_Pricing;

	const { loading, data } = useListingPricingSchedule({ listingId, isSchedulePriceEnabled });

	if (loading) {
		return (
			<div className={styles.loadingContainer}>
				<Loader />
			</div>
		);
	}

	return <SchedulePriceInner inventoryItem={inventoryItem} tableView={tableView} onSuccess={onSuccess} defaultValues={data} />;
};
